import React, {useEffect, useState} from 'react';
import styled, {css, keyframes} from 'styled-components';

export type ValidationBannerState =
    | 'hidden'
    | 'entering'
    | 'visible'
    | 'exiting';

export const useValidationBanner = (hasValidationErrors: boolean) => {
    const [debouncedHasValidationErrors, setDebouncedHasValidationErrors] =
        useState(false);
    const [validationBannerState, setValidationBannerState] =
        useState<ValidationBannerState>('hidden');

    useEffect(() => {
        const t = window.setTimeout(() => {
            setDebouncedHasValidationErrors(Boolean(hasValidationErrors));
        }, 300);
        return () => window.clearTimeout(t);
    }, [hasValidationErrors]);

    useEffect(() => {
        if (debouncedHasValidationErrors) {
            setValidationBannerState('entering');
            const t = window.setTimeout(() => {
                setValidationBannerState('visible');
            }, 220);
            return () => window.clearTimeout(t);
        }

        // allow exit animation before unmount
        setValidationBannerState((prev) => {
            if (prev === 'hidden') return 'hidden';
            return 'exiting';
        });
        const t = window.setTimeout(() => {
            setValidationBannerState('hidden');
        }, 220);
        return () => window.clearTimeout(t);
    }, [debouncedHasValidationErrors]);

    return {debouncedHasValidationErrors, validationBannerState};
};

export const ErrorHandler = ({
    validationBannerState,
}: {
    validationBannerState: ValidationBannerState;
}) => {
    if (validationBannerState === 'hidden') return null;

    return (
        <ValidationErrorText
            $isEntering={validationBannerState === 'entering'}
            $isExiting={validationBannerState === 'exiting'}>
            This preview may not reflect your current configuration.
            <br />
            Resolve the errors to update it.
        </ValidationErrorText>
    );
};

const bannerEnter = keyframes`
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const bannerExit = keyframes`
    from {
        opacity: 1;
        transform: translateY(0);
    }
    to {
        opacity: 0;
        transform: translateY(10px);
    }
`;

const ValidationErrorText = styled.div<{
    $isEntering: boolean;
    $isExiting: boolean;
}>`
    text-align: center;
    position: absolute;
    bottom: 0px;
    left: 0px;
    right: 0px;
    z-index: 5;
    padding: 10px 12px;
    background: rgba(211, 47, 47, 0.94);
    border: 1px solid #d32f2f;
    color: #fff;
    border-radius: 6px 6px 0 0;
    font-size: 13px;
    font-weight: 600;
    ${({$isEntering}) =>
        $isEntering
            ? css`
                  animation: ${bannerEnter} 220ms ease-out;
              `
            : ''}
    ${({$isExiting}) =>
        $isExiting
            ? css`
                  animation: ${bannerExit} 220ms ease-in;
              `
            : ''}
`;
